import React, { useState } from "react";
import { Link } from "react-router-dom";

const TOOL_LINKS = [
  { to: "/chat",         label: "Chat" },
  { to: "/deadlines",    label: "Deadlines" },
  { to: "/estimator",    label: "Estimator" },
  { to: "/scholarships", label: "Scholarships" },
  { to: "/decoder",      label: "Aid Decoder" },
  { to: "/compare",      label: "Compare" },
  { to: "/appeal",       label: "Appeal" },
  { to: "/loans",        label: "Loans" },
  { to: "/journey",      label: "The Journey" },
  { to: "/sidebyside",   label: "Side-by-Side" },
  { to: "/glossary",     label: "Glossary" },
];

const LEGAL = [
  { to: "/legal/privacy", label: "Privacy" },
  { to: "/legal/terms",   label: "Terms" },
];

/**
 * Landing-page footer. Light theme, matches the rest of the page
 * (#f9fafb) with a top border for separation.
 *
 * Colors are explicit hex (not Tailwind theme tokens) so the
 * .theme-light Tailwind overrides cannot accidentally flip them
 * if the parent theme ever changes.
 */
export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
    setTimeout(() => {
      window.location.href = `/deadlines?email=${encodeURIComponent(email)}`;
    }, 250);
  };

  return (
    <footer
      style={{ background: "#ffffff", borderTop: "1px solid #e5e7eb", color: "#6b7280" }}
      data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10">
        <div className="grid md:grid-cols-3 gap-10">
          {/* Brand */}
          <div>
            <Link to="/" className="inline-block mb-3" data-testid="footer-logo">
              <img
                src="/aidwaive-logo.png"
                alt="AidWaive"
                className="h-10 sm:h-11 w-auto object-contain select-none"
                draggable={false}
              />
            </Link>
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: "#6b7280" }}>
              Free financial-aid help for American families. No login. Nothing stored.
            </p>
            <p className="text-xs mt-6" style={{ color: "#9ca3af" }}>
              © {new Date().getFullYear()} AidWaive. Not affiliated with the U.S. Department of Education.
            </p>
          </div>

          {/* Tools + legal */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] mb-4" style={{ color: "#9ca3af" }}>Tools</div>
              <ul className="space-y-2">
                {TOOL_LINKS.map(t => (
                  <li key={t.to}>
                    <Link
                      to={t.to}
                      className="text-sm transition-colors"
                      style={{ color: "#6b7280" }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#111928")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#6b7280")}
                      data-testid={`footer-tool-${t.to.slice(1)}`}>
                      {t.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] mb-4" style={{ color: "#9ca3af" }}>Legal</div>
              <ul className="space-y-2">
                {LEGAL.map(l => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="text-sm transition-colors"
                      style={{ color: "#6b7280" }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = "#111928")}
                      onMouseLeave={(e) => (e.currentTarget.style.color = "#6b7280")}
                      data-testid={`footer-${l.label.toLowerCase()}`}>
                      {l.label}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href="https://studentaid.gov" target="_blank" rel="noopener noreferrer"
                    className="text-sm transition-colors"
                    style={{ color: "#6b7280" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#111928")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#6b7280")}
                    data-testid="footer-studentaid">
                    studentaid.gov ↗
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Newsletter */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] mb-4" style={{ color: "#9ca3af" }}>Get deadline alerts</div>
            <p className="text-sm leading-relaxed mb-4" style={{ color: "#6b7280" }}>
              The only email AidWaive will ever send: deadline reminders for your state.
            </p>
            <form onSubmit={onSubmit} className="flex gap-2" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={submitted}
                className="flex-1 h-11 px-3 rounded-md text-sm outline-none transition-colors"
                style={{
                  background: "#ffffff",
                  border: "1px solid #e5e7eb",
                  color: "#111928",
                }}
                data-testid="footer-newsletter-email"
              />
              <button
                type="submit" disabled={submitted || !email}
                className="px-4 h-11 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: "#1d4ed8", color: "#ffffff" }}
                data-testid="footer-newsletter-submit">
                {submitted ? "Opening…" : "Subscribe"}
              </button>
            </form>
            <p className="text-[11px] mt-3" style={{ color: "#9ca3af" }}>
              Double opt-in. One-click unsubscribe.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
